/*
  <sp-cart-discount>: discount codes in the cart drawer (snippets/sp-cart-discount.liquid).

  Apply: POST /cart/update.js { discount: "<applied codes>,<new code>", sections }.
  Shopify answers with the cart; each code is listed in cart.discount_codes with
  an `applicable` flag. If the new code is not applicable (unknown, below the
  order minimum, customer-specific, expired) it is removed again with a second
  update so no dead code lingers, and an error is shown. Every successful
  response re-renders the drawer through Dawn's own CartDrawer.renderContents,
  so totals, Dawn's discount list and the chips come from one server state.
  Remove: same request with the code left out.

  One request at a time (aria-busy guards double taps); network and server
  errors show a message and leave the cart untouched.
*/
(() => {
  const config = () =>
    typeof fetchConfig === 'function'
      ? fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

  class SpCartDiscount extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-sp-discount-form]');
      this.input = this.querySelector('[data-sp-discount-input]');
      this.error = this.querySelector('[data-sp-discount-error]');
      if (!this.form || !this.input) return;

      // restore what the visitor typed if the previous attempt was rejected (the drawer re-rendered)
      const pending = SpCartDiscount.pending;
      if (pending) {
        SpCartDiscount.pending = null;
        const details = this.querySelector('[data-sp-discount-details]');
        if (details && pending.message) details.open = true;
        this.input.value = pending.value || '';
        if (pending.message) this.showError(pending.message);
        if (pending.focus) this.input.focus({ preventScroll: true });
      }

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.apply(this.input.value.trim());
      });
      // remove buttons sit in the bill (sp-cart-totals), outside this element
      this.onDocClick = (event) => {
        const button = event.target.closest('cart-drawer [data-sp-discount-remove]');
        if (button && this.isConnected) this.remove(button.dataset.spDiscountRemove);
      };
      document.addEventListener('click', this.onDocClick);
      this.input.addEventListener('input', () => this.clearError());
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
    }

    get busy() {
      return this.getAttribute('aria-busy') === 'true';
    }

    set busy(value) {
      this.setAttribute('aria-busy', value ? 'true' : 'false');
      const button = this.querySelector('[data-sp-discount-apply]');
      if (button) button.disabled = !!value;
    }

    appliedCodes() {
      return Array.from(document.querySelectorAll('cart-drawer [data-sp-discount-remove]')).map((b) => b.dataset.spDiscountRemove);
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
    }

    clearError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
      this.input.removeAttribute('aria-invalid');
    }

    async apply(code) {
      if (this.busy) return;
      if (!code) {
        this.input.focus();
        return;
      }
      const current = this.appliedCodes();
      if (current.some((c) => c.toLowerCase() === code.toLowerCase())) {
        this.showError(this.dataset.msgDuplicate || 'That code is already applied.');
        return;
      }
      this.clearError();
      this.busy = true;
      try {
        const cart = await this.update([...current, code]);
        const entry = (cart.discount_codes || []).find((c) => c.code.toLowerCase() === code.toLowerCase());
        if (!entry || !entry.applicable) {
          // reject: put the cart back to the codes that were applied before, then explain
          const reverted = await this.update(current);
          SpCartDiscount.pending = { value: code, message: this.dataset.msgInvalid || 'This code can’t be applied to your cart.', focus: true };
          this.render(reverted);
          return;
        }
        SpCartDiscount.pending = { value: '', message: '', focus: true };
        this.render(cart);
      } catch (error) {
        this.busy = false;
        this.showError(this.dataset.msgNetwork || 'Something went wrong. Please try again.');
      }
    }

    async remove(code) {
      if (this.busy) return;
      this.clearError();
      this.busy = true;
      try {
        const remaining = this.appliedCodes().filter((c) => c.toLowerCase() !== code.toLowerCase());
        const cart = await this.update(remaining);
        this.render(cart);
      } catch (error) {
        this.busy = false;
        this.showError(this.dataset.msgNetwork || 'Something went wrong. Please try again.');
      }
    }

    async update(codes) {
      const body = JSON.stringify({
        discount: codes.join(','),
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      });
      const response = await fetch(window.routes.cart_update_url, { ...config(), body });
      const cart = await response.json();
      if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'cart update failed');
      return cart;
    }

    render(cart) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function' && cart.sections) {
        drawer.renderContents(cart);
        return;
      }
      window.location.reload();
    }
  }

  SpCartDiscount.pending = null;
  if (!customElements.get('sp-cart-discount')) customElements.define('sp-cart-discount', SpCartDiscount);
})();
